import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { RefreshCw, Link2, Unlink, Plus, Camera } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { AvatarCropDialog } from '@/components/settings/AvatarCropDialog';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { getProxiedImageUrl } from '@/utils/imageProxy';
import { GroupedCreator } from '@/hooks/useGroupedCreatorsData';

interface CreatorEditDialogProps {
  creator: GroupedCreator | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PLATFORMS = ['tiktok', 'instagram', 'youtube'] as const;

const platformColors: Record<string, string> = {
  tiktok: 'bg-[#00f2ea]/10 text-[#00f2ea]',
  instagram: 'bg-[#fd1d1d]/10 text-[#fd1d1d]',
  youtube: 'bg-[#ff0000]/10 text-[#ff0000]',
};

interface AccountEdits {
  username: string;
  follower_count: string;
  bio: string;
  avatar_url: string;
}

export const CreatorEditDialog: React.FC<CreatorEditDialogProps> = ({ creator, open, onOpenChange }) => {
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  
  const [editingAccounts, setEditingAccounts] = useState<Record<string, AccountEdits>>({});
  const [linkedOverrides, setLinkedOverrides] = useState<Record<string, boolean>>({});
  const [newAccounts, setNewAccounts] = useState<Record<string, string>>({ tiktok: '', instagram: '', youtube: '' });
  const [addingPlatform, setAddingPlatform] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (creator) {
      setDisplayName(creator.display_name || creator.primary_name || '');
      setAvatarUrl(creator.avatar_url || '');
      // Initialize editable fields from existing accounts
      const edits: Record<string, AccountEdits> = {};
      creator.creators.forEach(acc => {
        edits[acc.id] = {
          username: acc.username,
          follower_count: String(acc.follower_count ?? 0),
          bio: acc.bio || '',
          avatar_url: acc.avatar_url || '',
        };
      });
      setEditingAccounts(edits);
      setNewAccounts({ tiktok: '', instagram: '', youtube: '' });
      setLinkedOverrides({});
    }
  }, [creator]);

  const updateAccountField = (id: string, field: keyof AccountEdits, value: string) => {
    setEditingAccounts(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
      setIsCropOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropConfirm = async (blob: Blob) => {
    if (!creator) return;
    setIsCropOpen(false);
    setCropImageSrc(null);
    try {
      const filePath = `creators/${creator.id}/${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, blob, { contentType: 'image/jpeg', upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(filePath);
      await supabase.from('creator_groups').update({ avatar_url: publicUrl }).eq('id', creator.id);
      setAvatarUrl(publicUrl);
      queryClient.invalidateQueries({ queryKey: ['grouped-creators-admin'] });
      toast({ title: 'Avatar Updated', description: 'Profile picture saved.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to upload avatar.', variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    if (!creator) return;
    setSaving(true);
    try {
      // Update display name
      if (creator.is_grouped) {
        await supabase.from('creator_groups').update({ display_name: displayName }).eq('id', creator.id);
      } else {
        await supabase.from('creators').update({ display_name: displayName }).eq('id', creator.id);
      }

      // Update each edited account
      for (const account of creator.creators) {
        const edits = editingAccounts[account.id];
        if (!edits) continue;
        await supabase.from('creators').update({
          username: edits.username,
          follower_count: parseInt(edits.follower_count) || 0,
          bio: edits.bio || null,
          avatar_url: edits.avatar_url || null,
        }).eq('id', account.id);
      }

      // Recalculate group total followers
      if (creator.is_grouped) {
        const newTotal = creator.creators.reduce((sum, acc) => {
          const edits = editingAccounts[acc.id];
          return sum + (parseInt(edits?.follower_count || '0') || 0);
        }, 0);
        await supabase.from('creator_groups').update({ total_followers: newTotal }).eq('id', creator.id);
      }

      toast({ title: 'Saved', description: 'Creator updated successfully.' });
      queryClient.invalidateQueries({ queryKey: ['grouped-creators-admin'] });
      onOpenChange(false);
    } catch {
      toast({ title: 'Error', description: 'Failed to save.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleLinkAccount = async (accountId: string) => {
    setLinkingId(accountId);
    try {
      const { error } = await supabase.functions.invoke('link-creator-accounts', {
        body: { creatorIds: [accountId] },
      });
      if (error) throw error;
      setLinkedOverrides(prev => ({ ...prev, [accountId]: true }));
      toast({ title: 'Linked', description: 'Account enriched via API.' });
      queryClient.invalidateQueries({ queryKey: ['grouped-creators-admin'] });
    } catch {
      toast({ title: 'Error', description: 'Failed to link account.', variant: 'destructive' });
    } finally {
      setLinkingId(null);
    }
  };

  const handleUnlinkAccount = async (accountId: string) => {
    if (!creator) return;
    setUnlinkingId(accountId);
    try {
      const { error } = await supabase.from('creators').update({ is_linked: false }).eq('id', accountId);
      if (error) throw error;
      setLinkedOverrides(prev => ({ ...prev, [accountId]: false }));
      toast({ title: 'Account Unlinked', description: 'Account marked as unlinked. Use Link to re-enrich.' });
      queryClient.invalidateQueries({ queryKey: ['grouped-creators-admin'] });
    } catch {
      toast({ title: 'Error', description: 'Failed to unlink account.', variant: 'destructive' });
    } finally {
      setUnlinkingId(null);
    }
  };

  const handleAddAccount = async (platform: string) => {
    const username = newAccounts[platform]?.trim();
    if (!username || !creator) return;
    setAddingPlatform(platform);
    try {
      const groupId = creator.is_grouped ? creator.id : null;

      // If ungrouped single creator, we need to create a group first
      let targetGroupId = groupId;
      if (!targetGroupId) {
        const { data: newGroup, error: groupErr } = await supabase.from('creator_groups').insert({
          primary_name: creator.primary_name,
          display_name: displayName || creator.primary_name,
          avatar_url: creator.avatar_url,
          total_followers: creator.total_followers,
        }).select().single();
        if (groupErr) throw groupErr;
        targetGroupId = newGroup.id;

        // Attach existing creator to group
        await supabase.from('creators').update({ creator_group_id: targetGroupId }).eq('id', creator.creators[0].id);
      }

      const { error } = await supabase.from('creators').insert({
        platform,
        username,
        platform_id: username,
        creator_group_id: targetGroupId,
        is_linked: false,
      });
      if (error) throw error;

      setNewAccounts(prev => ({ ...prev, [platform]: '' }));
      toast({ title: 'Account Added', description: `${platform} account added. Use Link to enrich via API.` });
      queryClient.invalidateQueries({ queryKey: ['grouped-creators-admin'] });
      onOpenChange(false); // Close to refresh data
    } catch {
      toast({ title: 'Error', description: 'Failed to add account.', variant: 'destructive' });
    } finally {
      setAddingPlatform(null);
    }
  };

  if (!creator) return null;

  const existingPlatforms = new Set(creator.creators.map(c => c.platform));

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[540px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Creator</DialogTitle>
            <DialogDescription>
              Edit display name, manage social accounts, or manually update fields
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Avatar */}
            <div className="flex justify-center">
              <div className="relative">
                <Avatar className="h-24 w-24 border-2 border-border">
                  {avatarUrl && (
                    <AvatarImage src={getProxiedImageUrl(avatarUrl)} className="object-cover" />
                  )}
                  <AvatarFallback className="text-2xl font-semibold bg-muted">
                    {(displayName || creator?.primary_name || '?').slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:bg-primary/90 transition-colors"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarFileChange}
                />
              </div>
            </div>

            {/* Display Name */}
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Creator display name"
              />
            </div>

            {/* Social Accounts */}
            <div className="space-y-3">
              <Label>Social Accounts</Label>

              {PLATFORMS.map((platform) => {
                const account = creator.creators.find(c => c.platform === platform);

                if (account) {
                  const edits = editingAccounts[account.id];
                  if (!edits) return null;
                  const isLinked = linkedOverrides[account.id] ?? account.is_linked;

                  return (
                    <div key={account.id} className="p-3 rounded-lg border border-border bg-muted/30 space-y-3">
                      {/* Header row */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            {edits.avatar_url && (
                              <AvatarImage src={getProxiedImageUrl(edits.avatar_url)} className="object-cover" />
                            )}
                            <AvatarFallback className="text-xs">
                              {platform.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <Badge variant="secondary" className={`text-xs ${platformColors[platform]}`}>
                            {platform}
                          </Badge>
                          {!isLinked && (
                            <Badge variant="outline" className="text-xs text-muted-foreground">unlinked</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {!isLinked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 text-xs gap-1"
                              disabled={linkingId === account.id}
                              onClick={() => handleLinkAccount(account.id)}
                            >
                              {linkingId === account.id ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <Link2 className="h-3 w-3" />
                              )}
                              Link
                            </Button>
                          )}
                          {isLinked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 text-xs gap-1 text-destructive hover:text-destructive"
                              disabled={unlinkingId === account.id}
                              onClick={() => handleUnlinkAccount(account.id)}
                            >
                              {unlinkingId === account.id ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <Unlink className="h-3 w-3" />
                              )}
                              Unlink
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Editable fields */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Username</Label>
                          <Input
                            value={edits.username}
                            onChange={(e) => updateAccountField(account.id, 'username', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Followers</Label>
                          <Input
                            type="number"
                            value={edits.follower_count}
                            onChange={(e) => updateAccountField(account.id, 'follower_count', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Bio</Label>
                        <Textarea
                          value={edits.bio}
                          onChange={(e) => updateAccountField(account.id, 'bio', e.target.value)}
                          className="text-sm min-h-[60px]"
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Avatar URL</Label>
                        <Input
                          value={edits.avatar_url}
                          onChange={(e) => updateAccountField(account.id, 'avatar_url', e.target.value)}
                          className="h-8 text-sm"
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  );
                }

                // Empty platform slot
                return (
                  <div key={platform} className="p-3 rounded-lg border border-dashed border-border bg-muted/10 flex items-center gap-3">
                    <Badge variant="secondary" className={`text-xs opacity-50 ${platformColors[platform]}`}>
                      {platform}
                    </Badge>
                    <span className="text-xs text-muted-foreground">(not added)</span>
                    <div className="flex-1" />
                    <Input
                      value={newAccounts[platform] || ''}
                      onChange={(e) => setNewAccounts(prev => ({ ...prev, [platform]: e.target.value }))}
                      placeholder="username"
                      className="h-8 text-sm max-w-[160px]"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-1"
                      disabled={!newAccounts[platform]?.trim() || addingPlatform === platform}
                      onClick={() => handleAddAccount(platform)}
                    >
                      {addingPlatform === platform ? (
                        <RefreshCw className="h-3 w-3 animate-spin" />
                      ) : (
                        <Plus className="h-3 w-3" />
                      )}
                      Add
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {cropImageSrc && (
        <AvatarCropDialog
          open={isCropOpen}
          imageSrc={cropImageSrc}
          onConfirm={handleCropConfirm}
          onCancel={() => { setIsCropOpen(false); setCropImageSrc(null); }}
        />
      )}
    </>
  );
};
